import { useState, useEffect, useRef, useCallback } from 'react'

type PreviewFn = (payload: string, format: 'json' | 'xml', signal: AbortSignal) => Promise<Blob>

interface PreviewState {
  pdfUrl: string | null
  loading: boolean
  warnings: string[]
  error: string | null
}

export function usePreviewRender(
  templateVersionId: string | null,
  previewFn: PreviewFn
): PreviewState & { trigger: (payload: string, format: 'json' | 'xml') => void } {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [warnings] = useState<string[]>([])

  const pendingUrl = useRef<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const genRef = useRef(0)

  const revokePending = useCallback(() => {
    if (pendingUrl.current) {
      URL.revokeObjectURL(pendingUrl.current)
      pendingUrl.current = null
    }
  }, [])

  const trigger = useCallback(async (payload: string, format: 'json' | 'xml') => {
    if (!templateVersionId) return

    abortRef.current?.abort()
    abortRef.current = new AbortController()
    const gen = ++genRef.current

    setLoading(true)
    setError(null)

    try {
      const blob = await previewFn(payload, format, abortRef.current.signal)
      if (gen !== genRef.current) return
      revokePending()
      const url = URL.createObjectURL(blob)
      pendingUrl.current = url
      setPdfUrl(url)
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return
      if (gen !== genRef.current) return
      setError(err instanceof Error ? err.message : 'Preview failed')
    } finally {
      if (gen === genRef.current) setLoading(false)
    }
  }, [templateVersionId, previewFn, revokePending])

  useEffect(() => {
    return () => {
      revokePending()
      abortRef.current?.abort()
    }
  }, [revokePending])

  return { pdfUrl, loading, warnings, error, trigger }
}
